import React from 'react';
import { Button } from '../../../components/ui/button';
import { GripVertical, Truck, Plus, PlusCircle, Pencil, Trash2, Scale, Eye, X } from 'lucide-react';
import { getKgDiffColor, truckHasEmptyFields, formatDateToDDMMAAAA, formatTons } from '../utils/helpers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Componente de fila sortable
const SortableTruckRow = ({ 
  truck, 
  idx, 
  dispatch,
  companies,
  canSeePrice, 
  canTakeActions, 
  isMaster, 
  isAdmin,
  onAddTruckData,
  onModifyTruckData,
  onRemoveTruck,
  onEditTruckAssignment,
  onUploadPesaje,
  onViewPesaje,
  onDeletePesaje
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: truck.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const tonDiff = ((truck.kg_loaded || 0) - (truck.kg_unloaded || 0)) / 1000;
  const diffColor = getKgDiffColor(truck.kg_loaded, truck.kg_unloaded);
  const hasEmptyFields = truckHasEmptyFields(truck);
  const isFinished = dispatch.status === 'Finalizado';

  return (
    <tr 
      ref={setNodeRef}
      style={style}
      className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'} ${isDragging ? 'bg-blue-100 shadow-lg' : 'hover:bg-blue-50/50'} transition-colors`} 
      data-testid={`truck-row-${truck.id}`}
    >
      {/* Columna de drag handle - Solo MASTER puede reordenar */}
      {isMaster && !isFinished && (
        <td className="px-1 py-1.5 text-center w-6">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-0.5"
            title="Arrastrar para reordenar"
          >
            <GripVertical size={12} />
          </button>
        </td>
      )}
      <td className="px-1.5 py-1.5 text-center text-slate-500 text-xs font-mono">{truck.order}</td>
      <td className="px-1.5 py-1.5 text-center font-mono text-slate-600 text-xs">{formatDateToDDMMAAAA(truck.load_date) || '-'}</td>
      <td className="px-1.5 py-1.5 text-center font-mono text-slate-600 text-xs">{formatDateToDDMMAAAA(truck.delivery_date) || '-'}</td>
      <td className="px-1.5 py-1.5 font-medium text-slate-700 truncate max-w-[120px] text-xs">
        {companies?.find(c => c.id === truck.company_id)?.alias || truck.company_alias || truck.company_name}
      </td>
      <td className="px-1.5 py-1.5 font-mono text-slate-500 text-xs">{truck.transport_invoice || '-'}</td>
      <td className="px-1.5 py-1.5 font-mono font-semibold text-slate-700 text-xs">{truck.license_plate}</td>
      <td className="px-1.5 py-1.5 font-mono text-slate-600 text-xs">{truck.trailer_plate || '-'}</td>
      <td className="px-1.5 py-1.5 text-slate-600 truncate max-w-[90px] text-xs">{truck.driver_name}</td>
      <td className="px-1.5 py-1.5 text-right font-mono text-slate-700 text-xs font-medium">{truck.kg_loaded ? formatTons(truck.kg_loaded) : '—'}</td>
      <td className="px-1.5 py-1.5 text-right font-mono text-slate-700 text-xs">${truck.freight_value?.toLocaleString() || '-'}</td>
      <td className="px-1.5 py-1.5 text-slate-600 text-xs">{truck.broker || '-'}</td>
      <td className="px-1.5 py-1.5 text-slate-600 truncate max-w-[80px] text-xs">{truck.destination || '-'}</td>
      <td className="px-1.5 py-1.5 font-mono text-slate-500 text-xs">{truck.sale_invoice || '-'}</td>
      <td className="px-1.5 py-1.5 text-slate-600 truncate max-w-[70px] text-xs">{truck.commune || '-'}</td>
      {canSeePrice && (
        <td className="px-1.5 py-1.5 text-right font-mono text-slate-700 text-xs">{truck.sale_price?.toLocaleString() || '-'}</td>
      )}
      <td className="px-1.5 py-1.5 font-mono text-slate-500 text-xs">{truck.guide_number || '-'}</td>
      <td className="px-1.5 py-1.5 text-right font-mono text-slate-700 text-xs font-medium">{truck.kg_unloaded ? formatTons(truck.kg_unloaded) : '—'}</td>
      <td className={`px-1.5 py-1.5 text-right font-mono font-bold text-xs ${diffColor}`}>
        {truck.kg_loaded && truck.kg_unloaded ? tonDiff.toLocaleString('es-CL', {minimumFractionDigits:2, maximumFractionDigits:2}) : '—'}
      </td>
      {/* Acciones de línea - Siempre visibles, gris cuando deshabilitados */}
      {canTakeActions && (
        <td className="px-1 py-1 bg-transparent">
          <div className="flex items-center justify-end gap-0.5">
            {/* Pesaje de descarga */}
            {truck.pesaje_url ? (
              <>
                <button 
                  onClick={() => onViewPesaje(truck.pesaje_url)}
                  className="h-5 w-5 flex items-center justify-center rounded transition-colors text-purple-600 hover:bg-purple-100 cursor-pointer"
                  title="Ver Pesaje"
                  data-testid={`view-pesaje-${truck.id}`}
                >
                  <Eye size={13} />
                </button>
                {isMaster && !isFinished && (
                  <button 
                    onClick={() => onDeletePesaje(dispatch.id, truck.id)}
                    className="h-5 w-5 flex items-center justify-center rounded transition-colors text-orange-500 hover:bg-orange-100 cursor-pointer"
                    title="Eliminar Pesaje"
                    data-testid={`delete-pesaje-${truck.id}`}
                  >
                    <X size={13} />
                  </button>
                )}
              </>
            ) : (
              <button 
                onClick={() => isMaster && onUploadPesaje(dispatch.id, truck.id)}
                className={`h-5 w-5 flex items-center justify-center rounded transition-colors ${
                  isMaster ? 'text-purple-600 hover:bg-purple-100 cursor-pointer' : 'text-slate-300 cursor-not-allowed'
                }`}
                title={isMaster ? "Subir Pesaje Descarga" : "No disponible"}
                disabled={!isMaster}
                data-testid={`upload-pesaje-${truck.id}`}
              >
                <Scale size={13} />
              </button>
            )}
            {/* Agregar Datos - Solo si hay campos vacíos y no está finalizado */}
            <button 
              onClick={() => hasEmptyFields && !isFinished && isMaster && onAddTruckData(dispatch, truck)} 
              className={`h-5 w-5 flex items-center justify-center rounded transition-colors ${
                hasEmptyFields && !isFinished && isMaster
                  ? 'text-emerald-600 hover:bg-emerald-100 cursor-pointer' 
                  : 'text-slate-300 cursor-not-allowed'
              }`}
              title={hasEmptyFields && !isFinished && isMaster ? "Agregar Datos" : "No disponible"}
              disabled={!hasEmptyFields || isFinished || !isMaster}
            >
              <PlusCircle size={13} />
            </button>
            {/* Modificar Datos - Solo MASTER y no finalizado */}
            <button 
              onClick={() => !isFinished && isMaster && onModifyTruckData(dispatch, truck)} 
              className={`h-5 w-5 flex items-center justify-center rounded transition-colors ${
                !isFinished && isMaster
                  ? 'text-blue-600 hover:bg-blue-100 cursor-pointer' 
                  : !isFinished && isAdmin 
                    ? 'text-blue-600 hover:bg-blue-100 cursor-pointer'
                    : 'text-slate-300 cursor-not-allowed'
              }`}
              title={!isFinished ? (isMaster ? "Modificar Datos" : "Editar Asignación") : "No disponible"}
              disabled={isFinished}
            >
              <Pencil size={13} />
            </button>
            {/* Eliminar - Solo MASTER y no finalizado */}
            <button 
              onClick={() => !isFinished && isMaster && onRemoveTruck(dispatch.id, truck.id)} 
              className={`h-5 w-5 flex items-center justify-center rounded transition-colors ${
                !isFinished && isMaster
                  ? 'text-red-500 hover:bg-red-100 cursor-pointer' 
                  : 'text-slate-300 cursor-not-allowed'
              }`}
              title={!isFinished && isMaster ? "Eliminar Camión" : "No disponible"}
              disabled={isFinished || !isMaster}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

const TrucksTable = ({
  dispatch,
  companies,
  currentUser,
  onAssignTruck,
  onAddTruckData,
  onModifyTruckData,
  onRemoveTruck,
  onEditTruckAssignment,
  onReorderTrucks,
  onUploadPesaje,
  onViewPesaje,
  onDeletePesaje
}) => {
  const trucksList = dispatch.trucks || [];
  const canAssignMore = trucksList.length < (dispatch.truck_count || 1);
  const isAdmin = currentUser.role === 'ADMIN';
  const isMaster = currentUser.role === 'MASTER';
  const isDev = currentUser.role === 'DESARROLLO';
  const effectiveRole = isDev && currentUser.simulated_role ? currentUser.simulated_role : currentUser.role;
  const effectiveIsMaster = effectiveRole === 'MASTER';
  const effectiveIsAdmin = effectiveRole === 'ADMIN';
  const canSeePrice = effectiveIsMaster;
  const canTakeActions = effectiveIsMaster || effectiveIsAdmin;
  const isFinished = dispatch.status === 'Finalizado';

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = trucksList.findIndex(t => t.id === active.id);
      const newIndex = trucksList.findIndex(t => t.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(trucksList, oldIndex, newIndex);
        // Actualizar el campo order de cada truck
        const updatedTrucks = newOrder.map((t, idx) => ({ ...t, order: idx + 1 }));
        onReorderTrucks(dispatch.id, updatedTrucks);
      }
    }
  };

  if (trucksList.length === 0) {
    return (
      <div className="p-4 text-center text-slate-500 bg-slate-50 text-xs">
        <Truck size={24} className="mx-auto mb-1 text-slate-300" />
        <p>No hay camiones asignados a este despacho</p>
        {canAssignMore && !isFinished && canTakeActions && (
          <Button onClick={() => onAssignTruck(dispatch)} variant="outline" size="sm" className="mt-2 h-6 text-xs">
            <Plus size={12} className="mr-1" /> Asignar primer camión
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="w-full text-[11px] border-collapse min-w-[1400px]">
          <thead>
            <tr className="bg-slate-700 text-white">
              {/* Th del drag handle - Solo MASTER */}
              {effectiveIsMaster && !isFinished && (
                <th className="w-6 bg-slate-700"></th>
              )}
              <th className="px-1.5 py-1.5 text-center font-medium border-r border-slate-600 whitespace-nowrap w-8">#</th>
              <th className="px-1.5 py-1.5 text-center font-medium border-r border-slate-600 whitespace-nowrap">F. CARGA</th>
              <th className="px-1.5 py-1.5 text-center font-medium border-r border-slate-600 whitespace-nowrap">F. DESCARGA</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">TRANSPORTE</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">FACT. TRANSP.</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">PATENTE</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">PAT. ACOPLADO</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">CHOFER</th>
              <th className="px-1.5 py-1.5 text-right font-medium border-r border-slate-600 whitespace-nowrap">TON. CARG.</th>
              <th className="px-1.5 py-1.5 text-right font-medium border-r border-slate-600 whitespace-nowrap">P. FLETE</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">CORREDOR</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">CLIENTE</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">FACT. VENTA</th>
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">COMUNA</th>
              {canSeePrice && (
                <th className="px-1.5 py-1.5 text-right font-medium border-r border-slate-600 whitespace-nowrap">PRECIO</th>
              )}
              <th className="px-1.5 py-1.5 text-left font-medium border-r border-slate-600 whitespace-nowrap">GUÍA</th>
              <th className="px-1.5 py-1.5 text-right font-medium border-r border-slate-600 whitespace-nowrap">TON. DESC.</th>
              <th className="px-1.5 py-1.5 text-right font-medium whitespace-nowrap">DIF.(T)</th>
              {canTakeActions && <th className="w-20 bg-slate-700"></th>}
            </tr>
          </thead>
          <tbody>
            <SortableContext items={trucksList.map(t => t.id)} strategy={verticalListSortingStrategy}>
              {trucksList.map((truck, idx) => (
                <SortableTruckRow
                  key={truck.id}
                  truck={truck}
                  idx={idx}
                  dispatch={dispatch}
                  companies={companies}
                  canSeePrice={canSeePrice}
                  canTakeActions={canTakeActions}
                  isMaster={effectiveIsMaster}
                  isAdmin={effectiveIsAdmin}
                  onAddTruckData={onAddTruckData}
                  onModifyTruckData={onModifyTruckData}
                  onRemoveTruck={onRemoveTruck}
                  onEditTruckAssignment={onEditTruckAssignment}
                  onUploadPesaje={onUploadPesaje}
                  onViewPesaje={onViewPesaje}
                  onDeletePesaje={onDeletePesaje}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
    </div>
  );
};

export default TrucksTable;
